import { AnalystChartXAxisKind } from 'src/app/enums/analystChartXAxisKind.enum';
import { AnalystHeatmapCategoryKind } from 'src/app/enums/analystHeatmapCategoryKind.enum';

export enum AnalystOutputType {
  Table = 0,
  Chart = 1,
  Clarification = 2,
  NarrowingOptions = 3,
}

/** Closed set of ways a too-large search result can be narrowed. Mirrors the BE enum numerically. */
export enum NarrowingDimensionKind {
  Site = 0,
  Seg = 1,
  Agent = 2,
  DateRange = 3,
}

/** Closed set of record kinds a narrowing offer's match count can describe. Mirrors the BE enum numerically. */
export enum NarrowingEntityKind {
  Agents = 0,
  LabResults = 1,
  RiskAssessments = 2,
  Segs = 3,
}

export enum AnalystColumnType {
  Text = 0,
  Number = 1,
  Date = 2,
  Boolean = 3,
}

export enum AnalystChartType {
  Line = 0,
  Bar = 1,
  Area = 2,
  Scatter = 3,
  Heatmap = 4,
}

/** Why a turn stopped. Mirrors Web.Api.Core/AiAnalyst/AnalystStopReason.cs numerically. Set on the
 * Ask response only - not persisted on the message, so it's undefined for anything loaded from chat
 * history rather than freshly received from an ask. */
export enum AnalystStopReason {
  Completed = 0,
  MaxIterationsReached = 1,
  WallClockExceeded = 2,
  ModelCallTimedOut = 3,
  Failed = 4,
  GroundingFailed = 5,
  OutputTruncated = 6,
}

export interface Citation {
  id: string;
  entityType: string;
  recordId: string | null;
  label: string;
  route: string;
}

/** Mirrors Web.Api.Core/AiAnalyst/CitationEntityTypes.cs - the full set of record kinds a citation
 * can point at. Kept here rather than inferred from data since the BE is the single source of truth
 * for which values exist. */
export const CitationEntityTypes = {
  Sample: 'Sample',
  LabResult: 'LabResult',
  Seg: 'Seg',
  Agent: 'Agent',
  RiskAssessment: 'RiskAssessment',
  SensorQuery: 'SensorQuery',
  IhQuery: 'IhQuery',
} as const;

export type CitationEntityType = (typeof CitationEntityTypes)[keyof typeof CitationEntityTypes];

export interface AnalystColumn {
  key: string;
  label: string;
  dataType: AnalystColumnType;
}

export interface AnalystTableRow {
  cells: string[];
  citationIds: string[];
}

export interface AnalystTable {
  title: string;
  columns: AnalystColumn[];
  rows: AnalystTableRow[];
  truncated?: boolean;
  totalRowCount?: number;
}

export interface AnalystChartPoint {
  x: string;
  y: number | null;
}

export interface AnalystChartSeries {
  name: string;
  unit: string;
  points: AnalystChartPoint[];
  citationIds: string[];
}

export interface AnalystChartHeatmap {
  xCategories: string[];
  yCategories: string[];
  values: (number | null)[][];
  unit: string;
  citationIds: string[];
  xCategoryKind?: AnalystHeatmapCategoryKind;
  yCategoryKind?: AnalystHeatmapCategoryKind;
}

export interface AnalystChart {
  title: string;
  chartType: AnalystChartType;
  xAxisLabel: string;
  yAxisLabel: string;
  series: AnalystChartSeries[];
  truncated?: boolean;
  totalPointCount?: number;
  xAxisKind?: AnalystChartXAxisKind;
  heatmap?: AnalystChartHeatmap;
}

export interface AnalystClarification {
  question: string;
  options: string[];
  allowFreeText: boolean;
}

/**
 * System-computed, never model-authored — see AnalystOutputExtractor / AnalystOrchestrator on the
 * BE. `kind` is a closed, code-controlled set, so the FE derives the button's display label from
 * it locally (via its own i18n lookup) rather than trusting BE-sent text — there is no free-form
 * model output here to preserve untranslated the way `AnalystClarification.options` is.
 */
export interface AnalystNarrowingDimension {
  kind: NarrowingDimensionKind;
}

export interface AnalystNarrowingOptions {
  totalCount: number;
  entityKind: NarrowingEntityKind;
  dimensions: AnalystNarrowingDimension[];
}

export interface AnalystOutputBlock {
  type: AnalystOutputType;
  table?: AnalystTable;
  chart?: AnalystChart;
  clarification?: AnalystClarification;
  narrowingOptions?: AnalystNarrowingOptions;
}
